import type { Payment } from "../../../domain/entities/Payment";
import type { PaymentTransaction } from "../../../domain/entities/PaymentTransaction";
import type { PaymentProcessorPort } from "../../../application/ports/PaymentProcessorPort";
import type { PaymentVoucherPort } from "../../../application/ports/PaymentVoucherPort";
import type { PaymentTransactionHttp } from "../../mappers/paymentTransactionMapper";
import { mapPaymentTransaction } from "../../mappers/paymentTransactionMapper";

interface PaymentsProcessorHttpAdapterConfig {
  baseUrl: string;
  authorizationToken: string;
}

interface BffResponse<T> {
  success: boolean;
  data: T;
}

export class PaymentsProcessorHttpAdapter implements PaymentProcessorPort, PaymentVoucherPort {
  constructor(private readonly config: PaymentsProcessorHttpAdapterConfig) {}

  async process(payment: Payment): Promise<PaymentTransaction> {
    const url = this.composeUrl("payments/payments");

    const response = await fetch(url, {
      method: "POST",
      headers: this.buildJsonHeaders(),
      body: JSON.stringify(this.buildPaymentBody(payment)),
    });

    if (!response.ok) {
      throw new Error("No fue posible procesar el pago en este momento");
    }

    const rawPayload = (await response.json()) as
      | BffResponse<PaymentTransactionHttp>
      | PaymentTransactionHttp;
    const payload = this.unwrapBffData(rawPayload);
    return mapPaymentTransaction(payload);
  }

  async upload(params: { paymentMethodCode: string; transactionCode: string; file: File; email: string }): Promise<void> {
    const url = this.composeUrl(
      `payments/transactions/${encodeURIComponent(params.transactionCode)}/voucher`,
    );

    const formData = new FormData();
    formData.append("photo", params.file);
    formData.append("email", params.email);
    formData.append("payment_method_code", params.paymentMethodCode);

    const response = await fetch(url, {
      method: "PUT",
      headers: this.buildMultipartHeaders(),
      body: formData,
    });

    if (!response.ok) {
      throw new Error("No fue posible cargar el comprobante de pago");
    }
  }

  private buildPaymentBody(payment: Payment): Record<string, string | number> {
    const baseBody: Record<string, string | number> = {
      order_code: payment.orderId,
      payment_method_code: payment.method.code,
      token: payment.token,
      currency: payment.currency,
    };

    const cleanedMetadata = Object.entries(payment.metadata ?? {}).reduce<Record<string, string | number>>(
      (acc, [key, value]) => {
        if (typeof value === "number") {
          if (Number.isFinite(value)) {
            acc[key] = value;
          }
          return acc;
        }

        const trimmed = value.trim();

        if (trimmed.length > 0) {
          acc[key] = trimmed;
        }

        return acc;
      },
      {},
    );

    const body = {
      ...baseBody,
      ...cleanedMetadata,
    };

    if (typeof body.email === "string" && !body.sender_email) {
      body.sender_email = body.email;
      delete body.email;
    }

    return body;
  }

  private composeUrl(path: string): string {
    const normalizedBase = this.config.baseUrl.endsWith("/") ? this.config.baseUrl : `${this.config.baseUrl}/`;
    return new URL(path, normalizedBase).toString();
  }

  private unwrapBffData<T>(payload: T | BffResponse<T>): T {
    if (this.isBffResponse(payload)) {
      return payload.data;
    }

    return payload;
  }

  private isBffResponse<T>(payload: T | BffResponse<T>): payload is BffResponse<T> {
    return (
      typeof payload === "object" &&
      payload !== null &&
      "success" in payload &&
      "data" in payload
    );
  }

  private buildJsonHeaders(): HeadersInit {
    return {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: `${this.config.authorizationToken}`,
    };
  }

  private buildMultipartHeaders(): HeadersInit {
    return {
      Authorization: `${this.config.authorizationToken}`,
    };
  }
}
